import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Plus, Trash2, Copy, GripVertical, Eye, EyeOff, Undo2, Redo2, RotateCcw,
  Save, UploadCloud, ChevronDown, ChevronUp, Settings2, ListChecks, Palette, CalendarX2, Power,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const FIELD_TYPES = ["text", "email", "tel", "number", "date", "time", "textarea", "select", "checkbox", "tour", "vehicle", "duration", "guests"];

// Preset field library. standard_key is set only for real enquiry columns.
const PRESETS = [
  { k: "Full name", type: "text", std: "name", req: true },
  { k: "Email", type: "email", std: "email", req: true },
  { k: "Phone", type: "tel", std: "phone" },
  { k: "WhatsApp number", type: "tel", std: null },
  { k: "Tour destination", type: "tour", std: "tour" },
  { k: "Pickup location", type: "text", std: "pickup_location" },
  { k: "Drop-off location", type: "text", std: "dropoff_location" },
  { k: "Tour date", type: "date", std: "pickup_date", w: "half" },
  { k: "Pickup time", type: "time", std: "pickup_time", w: "half" },
  { k: "Return date", type: "date", std: "return_date", w: "half" },
  { k: "Return time", type: "time", std: "return_time", w: "half" },
  { k: "Passengers", type: "guests", std: "guests" },
  { k: "Children", type: "number", std: null, w: "half" },
  { k: "Luggage pieces", type: "number", std: null, w: "half" },
  { k: "Vehicle", type: "vehicle", std: "vehicle" },
  { k: "Tour duration", type: "duration", std: "duration_option" },
  { k: "Additional stops", type: "textarea", std: null },
  { k: "Special requests", type: "textarea", std: "message" },
  { k: "Hotel information", type: "text", std: null },
  { k: "Flight information", type: "text", std: null },
  { k: "Promo code", type: "text", std: null, w: "half" },
  { k: "Custom dropdown", type: "select", std: null },
  { k: "Custom text", type: "text", std: null },
  { k: "Custom checkbox", type: "checkbox", std: null },
];

const uid = () => `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

const Toggle = ({ on, onClick, testid, label }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-[var(--brand)]" : "bg-slate-300"}`} aria-label={label}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

const Section = ({ icon: Icon, title, children, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-[#0b132b]"><Icon className="w-4 h-4 text-[var(--brand)]" /> {title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
};

const Txt = ({ label, value, onChange, testid, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
    <input type={type} data-testid={testid} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
  </label>
);

export const BookTourManagerTab = () => {
  const [draft, setDraftRaw] = useState(null);
  const [live, setLive] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const past = useRef([]); const future = useRef([]);
  const saveTimer = useRef(null);
  const dragFrom = useRef(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/booking-form/full`, ax);
    setLive(r.data.live);
    setHistoryCount(r.data.history_count || 0);
    const d = r.data.draft || structuredClone(r.data.live);
    setDraftRaw(d);
    setSelectedId((d.fields || [])[0]?.id || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  const scheduleSave = useCallback((state) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setStatus("Saving…");
    saveTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/booking-form/draft`, state, ax); setStatus("All changes saved (draft)"); }
      catch { setStatus("Save failed"); }
    }, 700);
  }, []);

  const apply = useCallback((updater, record = true) => {
    setDraftRaw((prev) => {
      const next = typeof updater === "function" ? updater(structuredClone(prev)) : updater;
      if (record && prev) { past.current.push(prev); future.current = []; }
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const undo = () => { if (!past.current.length) return; setDraftRaw((prev) => { future.current.push(prev); const p = past.current.pop(); scheduleSave(p); return p; }); };
  const redo = () => { if (!future.current.length) return; setDraftRaw((prev) => { past.current.push(prev); const n = future.current.pop(); scheduleSave(n); return n; }); };
  const reset = () => { if (live) { past.current = []; future.current = []; const c = structuredClone(live); setDraftRaw(c); scheduleSave(c); toast.info("Reset to published version"); } };

  const publish = async () => {
    try { if (saveTimer.current) clearTimeout(saveTimer.current); await axios.put(`${API}/booking-form/draft`, draft, ax);
      const r = await axios.post(`${API}/booking-form/publish`, {}, ax);
      setLive(r.data.live); setHistoryCount(r.data.history_count || 0); setStatus("Published ✓"); toast.success("Booking form published — live now"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  const revert = async () => {
    try { const r = await axios.post(`${API}/booking-form/revert`, {}, ax);
      setLive(r.data.live); setHistoryCount(r.data.history_count || 0); const c = structuredClone(r.data.live); setDraftRaw(c); toast.success("Restored previous version"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to restore"); }
  };
  const toggle = async (key) => {
    try { const r = await axios.post(`${API}/booking-form/toggle`, { [key]: !draft[key] }, ax);
      setLive(r.data.live); setDraftRaw((p) => ({ ...p, [key]: r.data.live[key] })); }
    catch { toast.error("Could not update"); }
  };

  if (!draft) return <div className="p-8 text-sm text-slate-400">Loading booking manager…</div>;

  const fields = draft.fields || [];
  const selected = fields.find((f) => f.id === selectedId);
  const upField = (id, patch) => apply((d) => ({ ...d, fields: d.fields.map((f) => f.id === id ? { ...f, ...patch } : f) }));
  const upDesign = (patch) => apply((d) => ({ ...d, design: { ...d.design, ...patch } }));
  const upAvail = (patch) => apply((d) => ({ ...d, availability: { ...d.availability, ...patch } }));

  const addField = (p) => {
    const id = uid();
    const f = {
      id, type: p.type, standard_key: p.std,
      label_en: p.k, label_ar: "", placeholder_en: "", placeholder_ar: "",
      required: !!p.req, hidden: false, width: p.w || "full", step: 0,
      options: p.type === "select" ? [{ value: "option1", label_en: "Option 1", label_ar: "" }] : [],
      default: "", conditional: null,
    };
    apply((d) => ({ ...d, fields: [...d.fields, f] }));
    setSelectedId(id); setShowAdd(false);
  };
  const dupField = (id) => { const src = fields.find((f) => f.id === id); if (!src) return; const nf = { ...structuredClone(src), id: uid(), standard_key: null, label_en: `${src.label_en} (copy)` }; apply((d) => { const i = d.fields.findIndex((f) => f.id === id); const arr = [...d.fields]; arr.splice(i + 1, 0, nf); return { ...d, fields: arr }; }); setSelectedId(nf.id); };
  const delField = (id) => { apply((d) => ({ ...d, fields: d.fields.filter((f) => f.id !== id) })); if (selectedId === id) setSelectedId(null); };
  const onDrop = (toIdx) => { const from = dragFrom.current; if (from == null || from === toIdx) return; apply((d) => { const arr = [...d.fields]; const [m] = arr.splice(from, 1); arr.splice(toIdx, 0, m); return { ...d, fields: arr }; }); dragFrom.current = null; };

  return (
    <div className="space-y-5" data-testid="book-tour-manager">
      {/* Toolbar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="font-display font-bold text-lg text-[#0b132b]">Book a Tour Manager</h2>
          <p className="text-xs text-slate-500">{status || "Design your booking form — drag, edit, publish."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 mr-2"><span className="text-xs font-semibold text-slate-600">Section</span>
            <Toggle on={draft.enabled} onClick={() => toggle("enabled")} testid="bf-toggle-enabled" label="Enable booking section" /></div>
          <div className="flex items-center gap-2 mr-2"><span className="text-xs font-semibold text-slate-600">Submissions</span>
            <Toggle on={draft.submissions_enabled} onClick={() => toggle("submissions_enabled")} testid="bf-toggle-submissions" label="Enable submissions" /></div>
          <button data-testid="bf-undo" onClick={undo} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Undo"><Undo2 className="w-4 h-4" /></button>
          <button data-testid="bf-redo" onClick={redo} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Redo"><Redo2 className="w-4 h-4" /></button>
          <button data-testid="bf-reset" onClick={reset} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Reset to published"><RotateCcw className="w-4 h-4" /></button>
          <button data-testid="bf-restore" onClick={revert} disabled={!historyCount} className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-bold hover:bg-slate-50 disabled:opacity-40">Restore ({historyCount})</button>
          <button data-testid="bf-publish" onClick={publish} className="px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-xs font-bold flex items-center gap-1.5 hover:brightness-110"><UploadCloud className="w-4 h-4" /> Publish</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* Field list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#0b132b]">Form fields ({fields.length})</h3>
            <div className="relative">
              <button data-testid="bf-add-field" onClick={() => setShowAdd((s) => !s)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add field</button>
              {showAdd && (
                <div className="absolute right-0 top-9 z-20 w-64 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl p-2" data-testid="bf-preset-menu">
                  {PRESETS.map((p) => (
                    <button key={p.k} data-testid={`bf-preset-${p.k.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => addField(p)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-sm flex items-center justify-between">
                      <span>{p.k}</span><span className="text-[10px] text-slate-400 uppercase">{p.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2" data-testid="bf-field-list">
            {fields.map((f, idx) => (
              <div key={f.id} draggable onDragStart={() => { dragFrom.current = idx; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
                onClick={() => setSelectedId(f.id)} data-testid={`bf-row-${f.id}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer transition ${selectedId === f.id ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-slate-200 bg-white hover:border-slate-300"} ${f.hidden ? "opacity-50" : ""}`}>
                <GripVertical className="w-4 h-4 text-slate-300 shrink-0 cursor-grab" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0b132b] truncate">{f.label_en || f.id}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{f.type}{f.required ? " · required" : ""}</p>
                </div>
                <button data-testid={`bf-req-${f.id}`} onClick={(e) => { e.stopPropagation(); upField(f.id, { required: !f.required }); }} title="Required" className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${f.required ? "bg-[var(--brand)] text-white" : "bg-slate-100 text-slate-400"}`}>REQ</button>
                <button data-testid={`bf-hide-${f.id}`} onClick={(e) => { e.stopPropagation(); upField(f.id, { hidden: !f.hidden }); }} title="Show/Hide" className="text-slate-400 hover:text-slate-700">{f.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                <button data-testid={`bf-dup-${f.id}`} onClick={(e) => { e.stopPropagation(); dupField(f.id); }} title="Duplicate" className="text-slate-400 hover:text-slate-700"><Copy className="w-4 h-4" /></button>
                <button data-testid={`bf-del-${f.id}`} onClick={(e) => { e.stopPropagation(); delField(f.id); }} title="Delete" className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {fields.length === 0 && <p className="text-xs text-slate-400 py-6 text-center">No fields. Click "Add field" to start.</p>}
          </div>
        </div>

        {/* Editor + settings */}
        <div className="space-y-4">
          {selected ? (
            <Section icon={Settings2} title={`Edit field: ${selected.label_en}`} defaultOpen testid="bf-field-editor">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Type</span>
                  <select data-testid="bf-edit-type" value={selected.type} onChange={(e) => upField(selected.id, { type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Width</span>
                  <select data-testid="bf-edit-width" value={selected.width} onChange={(e) => upField(selected.id, { width: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <option value="full">Full row</option><option value="half">Half row</option>
                  </select>
                </label>
                <Txt label="Label (EN)" value={selected.label_en} onChange={(v) => upField(selected.id, { label_en: v })} testid="bf-edit-label-en" />
                <Txt label="Label (AR)" value={selected.label_ar} onChange={(v) => upField(selected.id, { label_ar: v })} testid="bf-edit-label-ar" />
                <Txt label="Placeholder (EN)" value={selected.placeholder_en} onChange={(v) => upField(selected.id, { placeholder_en: v })} testid="bf-edit-ph-en" />
                <Txt label="Placeholder (AR)" value={selected.placeholder_ar} onChange={(v) => upField(selected.id, { placeholder_ar: v })} testid="bf-edit-ph-ar" />
              </div>
              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="bf-edit-required" checked={selected.required} onChange={(e) => upField(selected.id, { required: e.target.checked })} className="w-4 h-4 accent-[var(--brand)]" /> Required</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="bf-edit-hidden" checked={selected.hidden} onChange={(e) => upField(selected.id, { hidden: e.target.checked })} className="w-4 h-4 accent-[var(--brand)]" /> Hidden</label>
              </div>
              {selected.type === "select" && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Dropdown options</p>
                  {(selected.options || []).map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={o.label_en} onChange={(e) => { const opts = [...selected.options]; opts[i] = { ...o, label_en: e.target.value, value: e.target.value }; upField(selected.id, { options: opts }); }}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" placeholder="Option label" data-testid={`bf-opt-${i}`} />
                      <button onClick={() => upField(selected.id, { options: selected.options.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button data-testid="bf-add-option" onClick={() => upField(selected.id, { options: [...(selected.options || []), { value: `option${(selected.options?.length || 0) + 1}`, label_en: `Option ${(selected.options?.length || 0) + 1}`, label_ar: "" }] })} className="text-xs font-bold text-[var(--brand)]">+ Add option</button>
                </div>
              )}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Conditional display (optional)</p>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="bf-cond-field" value={selected.conditional?.field || ""} onChange={(e) => upField(selected.id, { conditional: e.target.value ? { field: e.target.value, value: selected.conditional?.value || "" } : null })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <option value="">Always show</option>
                    {fields.filter((x) => x.id !== selected.id).map((x) => <option key={x.id} value={x.id}>Show if "{x.label_en}"</option>)}
                  </select>
                  <input data-testid="bf-cond-value" value={selected.conditional?.value || ""} disabled={!selected.conditional?.field} onChange={(e) => upField(selected.id, { conditional: { field: selected.conditional.field, value: e.target.value } })} placeholder="equals value…" className="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50" />
                </div>
              </div>
            </Section>
          ) : <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">Select a field to edit it, or add a new one.</div>}

          <Section icon={ListChecks} title="Layout & behaviour" testid="bf-layout-section">
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Form layout</span>
                <select data-testid="bf-layout" value={draft.layout} onChange={(e) => apply((d) => ({ ...d, layout: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="single">Single page</option><option value="multistep">Multi-step wizard</option>
                </select>
              </label>
            </div>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm"><Toggle on={draft.show_price_summary} onClick={() => apply((d) => ({ ...d, show_price_summary: !d.show_price_summary }))} testid="bf-toggle-price" /> Price summary</label>
              <label className="flex items-center gap-2 text-sm"><Toggle on={draft.whatsapp_enabled} onClick={() => apply((d) => ({ ...d, whatsapp_enabled: !d.whatsapp_enabled }))} testid="bf-toggle-whatsapp" /> WhatsApp button</label>
            </div>
          </Section>

          <Section icon={Palette} title="Design & text" testid="bf-design-section">
            <div className="grid grid-cols-2 gap-3">
              <Txt label="Title (EN)" value={draft.design?.title_en} onChange={(v) => upDesign({ title_en: v })} testid="bf-design-title-en" />
              <Txt label="Title (AR)" value={draft.design?.title_ar} onChange={(v) => upDesign({ title_ar: v })} testid="bf-design-title-ar" />
              <Txt label="Submit button (EN)" value={draft.design?.submit_text_en} onChange={(v) => upDesign({ submit_text_en: v })} testid="bf-design-submit-en" />
              <Txt label="Submit button (AR)" value={draft.design?.submit_text_ar} onChange={(v) => upDesign({ submit_text_ar: v })} testid="bf-design-submit-ar" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Accent / button colour</span>
                <input type="color" data-testid="bf-design-accent" value={draft.design?.accent_color || "#e11d2a"} onChange={(e) => upDesign({ accent_color: e.target.value, button_color: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200" /></label>
              <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Field shape</span>
                <select data-testid="bf-design-shape" value={draft.design?.field_shape} onChange={(e) => upDesign({ field_shape: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="rounded">Rounded</option><option value="pill">Pill</option><option value="square">Square</option></select></label>
            </div>
          </Section>

          <Section icon={CalendarX2} title="Availability rules" testid="bf-availability-section">
            <label className="flex items-center gap-2 text-sm"><Toggle on={draft.availability?.enabled} onClick={() => upAvail({ enabled: !draft.availability?.enabled })} testid="bf-avail-enabled" /> Enforce availability</label>
            <Txt label="Minimum booking notice (days)" type="number" value={draft.availability?.min_notice_days} onChange={(v) => upAvail({ min_notice_days: Number(v) || 0 })} testid="bf-avail-notice" />
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Blocked dates</span>
              <div className="flex items-center gap-2 mt-1">
                <input type="date" data-testid="bf-avail-block-input" id="bf-block-date" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <button data-testid="bf-avail-block-add" onClick={() => { const el = document.getElementById("bf-block-date"); if (el?.value) { upAvail({ blocked_dates: [...(draft.availability?.blocked_dates || []), el.value] }); el.value = ""; } }} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold">Block</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(draft.availability?.blocked_dates || []).map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-semibold px-2 py-1 rounded-full">{d}
                    <button onClick={() => upAvail({ blocked_dates: draft.availability.blocked_dates.filter((x) => x !== d) })}>×</button></span>
                ))}
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};
